<script>
import axios from 'axios'

const WINDOW_OPTIONS = [
  { value: 6, text: 'Last 6 hours' },
  { value: 24, text: 'Last 24 hours' },
  { value: 72, text: 'Last 3 days' },
  { value: 168, text: 'Last 7 days' }
]

const STATUS_ORDER = { crit: 0, warn: 1, ok: 2 }

export default {
  props: {
    device: {
      type: String,
      default: ''
    },
    baseUrl: {
      type: String,
      default: 'http://localhost:5000'
    },
    basePath: {
      type: String,
      default: '/monitoring/sdwan'
    }
  },
  data () {
    return {
      detail: {},
      pathstats: { series: {}, events: [] },
      hours: 24,
      windowOptions: WINDOW_OPTIONS,
      selectedVif: '',
      isBusy: false,
      loadError: ''
    }
  },
  computed: {
    systemInfo () {
      return this.detail.system_info || {}
    },
    uptime () {
      return this.detail.uptime || {}
    },
    health () {
      return this.detail.health || {}
    },
    sortedVifs () {
      const rank = (vif) => (vif.status in STATUS_ORDER ? STATUS_ORDER[vif.status] : 9)
      return [...(this.health.vifs || [])].sort((a, b) =>
        rank(a) - rank(b) || String(a.vif).localeCompare(String(b.vif)))
    },
    // Quality is measured per physical interface: one row, and one chart
    // series, per member path.
    sortedPaths () {
      const rows = []
      for (const vif of this.health.vifs || []) {
        for (const path of vif.paths || []) {
          rows.push({
            key: `${vif.vif} - ${path.if_name}`,
            vif: vif.vif,
            ifName: path.if_name,
            state: path.state,
            stateReason: path.state_reason,
            status: path.status || vif.status,
            metrics: path.metrics || {},
            traffic: path.traffic || {},
            measured: path.measured !== false && path.metrics &&
              typeof path.metrics.latency_ms === 'number'
          })
        }
      }
      const rank = (row) => (row.status in STATUS_ORDER ? STATUS_ORDER[row.status] : 9)
      return rows.sort((a, b) => rank(a) - rank(b) || a.key.localeCompare(b.key))
    },
    vifSelectOptions () {
      const names = new Set(Object.keys(this.pathstats.series || {}))
      this.sortedPaths.forEach((row) => names.add(row.key))
      return [...names].sort().map((name) => ({ value: name, text: name }))
    },
    chartSeries () {
      const points = (this.pathstats.series || {})[this.selectedVif] || []
      const pick = (key) => points.map((point) => [Date.parse(point.ts), point[key]])
      return [
        { name: 'Latency (ms)', type: 'line', data: pick('latency_ms') },
        { name: 'Jitter (ms)', type: 'line', data: pick('jitter_ms') },
        { name: 'Loss (%)', type: 'line', data: pick('loss_pct') }
      ]
    },
    chartOptions () {
      // Cap annotations so a busy device stays readable.
      const events = (this.pathstats.events || []).slice(0, 20)
      return {
        chart: { type: 'line', toolbar: { show: false }, animations: { enabled: false } },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#727cf5', '#0acf97', '#fa5c7c'],
        dataLabels: { enabled: false },
        // ApexCharts renders datetime axes in UTC unless told otherwise, which
        // put these charts 5 hours ahead of every table in the app.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: [
          { seriesName: 'Latency (ms)', title: { text: 'ms' }, min: 0 },
          { seriesName: 'Latency (ms)', show: false },
          { opposite: true, seriesName: 'Loss (%)', title: { text: '%' }, min: 0 }
        ],
        annotations: {
          xaxis: events.map((event) => ({
            x: Date.parse(event.ts),
            borderColor: '#ffbc00',
            label: {
              style: { color: '#fff', background: '#ffbc00' },
              // The marker sits at collection time; the device's own clock can
              // differ, so its timestamp is shown alongside.
              text: this.eventLabel(event)
            }
          }))
        },
        legend: { position: 'top' },
        tooltip: { x: { format: 'dd MMM HH:mm' } }
      }
    },
    hasChartData () {
      return ((this.pathstats.series || {})[this.selectedVif] || []).length > 1
    }
  },
  watch: {
    device () {
      this.loadAll()
    },
    hours () {
      this.loadStats()
    },
    selectedVif (nuevo) {
      if (nuevo && !((this.pathstats.series || {})[nuevo] || []).length) {
        this.loadStats()
      }
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    statsUrl () {
      // Only the charted interface is requested: a hub device carries 60+
      // member paths and fetching them all is slow and needlessly large.
      const base = `${this.baseUrl}${this.basePath}/pathstats/${encodeURIComponent(this.device)}`
      const params = [`hours=${this.hours}`]
      if (this.selectedVif) params.push(`path=${encodeURIComponent(this.selectedVif)}`)
      return `${base}?${params.join('&')}`
    },
    loadAll () {
      if (!this.device) return
      this.isBusy = true
      this.loadError = ''
      Promise.all([
        axios.get(`${this.baseUrl}${this.basePath}/device/${encodeURIComponent(this.device)}`),
        axios.get(this.statsUrl())
      ])
        .then(([detail, pathstats]) => {
          this.detail = this.parse(detail.data)
          this.pathstats = this.parse(pathstats.data)
          this.pickDefaultVif()
        })
        .catch((error) => {
          this.loadError = `Could not load SD-WAN device detail: ${error.message}`
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    loadStats () {
      if (!this.device) return
      axios
        .get(this.statsUrl())
        .then((response) => {
          const data = this.parse(response.data)
          // Merge so previously loaded series stay available while switching.
          this.pathstats = {
            ...data,
            series: { ...(this.pathstats.series || {}), ...(data.series || {}) }
          }
          this.pickDefaultVif()
        })
        .catch((error) => {
          this.loadError = `Could not load path statistics: ${error.message}`
        })
    },
    pickDefaultVif () {
      if (this.selectedVif) return
      // Default to the worst interface reported for the device.
      const worst = this.sortedPaths[0]
      const available = Object.keys(this.pathstats.series || {})
      this.selectedVif = (worst && worst.key) || available.sort()[0] || ''
    },
    onTabChange () {
      // A tab is hidden until selected, so a chart rendered inside it has no
      // width yet. Recalculating on activation keeps it inside the dialog.
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    eventLabel (event) {
      const motivo = event.reason || 'path event'
      const tick = String(event.tick || '')
      const hora = tick.split(' ')[1] || ''
      return hora ? `${motivo} · dev ${hora.slice(0, 5)}` : motivo
    },
    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    },
    metricText (value, unit) {
      return typeof value === 'number' ? `${value} ${unit}` : '—'
    },
    // Informative only: the firewall's own configured SLA decides what is
    // degraded, so the dashboard does not colour these against criteria of
    // its own.
    metricClass (value) {
      return typeof value === 'number' ? '' : 'text-muted'
    },
    // Event taxonomy: a real switch moved traffic to a different path;
    // "*-keep" re-evaluated and stayed; "initial" is the first selection.
    eventKind (event) {
      if (event.from && event.from !== 'N/A' && event.from !== event.to) return 'switch'
      if ((event.reason || '').endsWith('-keep')) return 'keep'
      if (event.reason === 'initial') return 'initial'
      return 'other'
    },
    eventIcon (event) {
      const icons = {
        switch: 'shuffle',
        keep: 'rotate-ccw',
        initial: 'play',
        other: 'activity'
      }
      return icons[this.eventKind(event)]
    },
    eventVariant (event) {
      return this.eventKind(event) === 'switch' ? 'warning' : 'muted'
    },
    pathStateVariant (state) {
      if (state === 'up') return 'success'
      if (state === 'down') return 'danger'
      return 'secondary'
    },
    vifStatusVariant (status) {
      const variants = { ok: 'success', warn: 'warning', crit: 'danger' }
      return variants[status] || 'secondary'
    }
  }
}
</script>

<template>
  <div>
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <div
      v-if="isBusy"
      class="text-center py-4"
    >
      <b-spinner class="align-middle" />
      <strong>&nbsp;&nbsp;Loading...</strong>
    </div>

    <div v-else>
      <!-- Facts -->
      <div class="row">
        <div class="col-md-8">
          <table class="table table-sm table-borderless mb-2">
            <tbody>
              <tr>
                <th class="text-muted">
                  Hostname
                </th>
                <td>{{ systemInfo.hostname || device }}</td>
                <th class="text-muted">
                  Serial
                </th>
                <td>{{ systemInfo.serial || '—' }}</td>
              </tr>
              <tr>
                <th class="text-muted">
                  Model
                </th>
                <td>{{ systemInfo.model || '—' }}</td>
                <th class="text-muted">
                  Version
                </th>
                <td>{{ systemInfo.sw_version || '—' }}</td>
              </tr>
              <tr>
                <th class="text-muted">
                  IP
                </th>
                <td>{{ systemInfo.ip || '—' }}</td>
                <th class="text-muted">
                  Uptime
                </th>
                <td>{{ uptime.uptime_text || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-md-4">
          <div class="card bg-light mb-2">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                Tunnels
              </h5>
              <h4 class="mb-0">
                <template v-if="typeof health.tunnels_total === 'number'">
                  {{ health.tunnels_up }}/{{ health.tunnels_total }} up
                </template>
                <span
                  v-else
                  class="text-muted font-size-13"
                >not reported</span>
              </h4>
              <span class="text-muted font-size-11">last seen {{ formatDate(health.last_seen) }}</span>
              <div
                v-if="health.sla && typeof health.sla.latency_ms === 'number'"
                class="text-muted font-size-11 mt-1"
              >
                SLA configured on the device: latency ≤ {{ health.sla.latency_ms }} ms ·
                jitter ≤ {{ health.sla.jitter_ms }} ms ·
                loss ≤ {{ health.sla.loss_pct }} %
              </div>
            </div>
          </div>
        </div>
      </div>

      <b-tabs
        content-class="pt-2"
        small
        @activate-tab="onTabChange"
      >
        <!-- Current paths -->
        <b-tab active>
          <template v-slot:title>
            <feather
              type="git-branch"
              class="icon-xs mr-1"
            />
            Paths ({{ sortedPaths.length }})
          </template>
          <div
            v-if="sortedPaths.length"
            class="table-responsive"
            style="max-height: min(420px, 45vh); overflow-y: auto;"
          >
            <table class="table table-sm table-hover font-size-12 mb-0">
              <thead>
                <tr class="text-muted">
                  <th>
                    Interface
                    <feather
                      v-b-tooltip.hover
                      type="help-circle"
                      class="icon-xs help-tip"
                      title="Physical interface or tunnel that is a member of the SD-WAN virtual interface. Quality is measured independently on each one. Click to chart it."
                    />
                  </th>
                  <th>VIF</th>
                  <th>State</th>
                  <th class="text-right">
                    Latency
                  </th>
                  <th class="text-right">
                    Jitter
                  </th>
                  <th class="text-right">
                    Loss
                  </th>
                  <th>
                    Per traffic
                    <feather
                      v-b-tooltip.hover
                      type="help-circle"
                      class="icon-xs help-tip"
                      title="Latency seen by the application traffic on this interface. Informative only: it exists only while that traffic class is active and never raises alerts."
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedPaths"
                  :key="row.key"
                >
                  <td class="font-weight-bold text-nowrap">
                    <a
                      href="javascript:void(0)"
                      @click="selectedVif = row.key"
                    >{{ row.ifName }}</a>
                  </td>
                  <td class="text-muted text-nowrap">
                    {{ row.vif }}
                  </td>
                  <td class="text-nowrap">
                    <b-badge :variant="pathStateVariant(row.state)">
                      {{ row.state || '?' }}
                    </b-badge>
                    <span
                      v-if="row.stateReason"
                      class="text-muted ml-1"
                    >{{ row.stateReason }}</span>
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.latency_ms)"
                  >
                    {{ metricText(row.metrics.latency_ms, 'ms') }}
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.jitter_ms)"
                  >
                    {{ metricText(row.metrics.jitter_ms, 'ms') }}
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.loss_pct)"
                  >
                    {{ metricText(row.metrics.loss_pct, '%') }}
                  </td>
                  <td class="text-muted text-nowrap">
                    {{ metricText(row.traffic.latency_ms, 'ms') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="text-muted py-3 text-center"
          >
            No current path data (HA-passive members report no active vifs).
          </div>
        </b-tab>

        <!-- Quality chart -->
        <b-tab>
          <template v-slot:title>
            <feather
              type="trending-up"
              class="icon-xs mr-1"
            />
            Path quality
          </template>
          <div class="d-flex align-items-center flex-wrap mb-2">
            <label class="mb-0 mr-2 font-size-12 text-muted">VIF</label>
            <b-form-select
              v-model="selectedVif"
              size="sm"
              :options="vifSelectOptions"
              style="max-width: 170px;"
              class="mr-3"
            />
            <label class="mb-0 mr-2 font-size-12 text-muted">Window</label>
            <b-form-select
              v-model="hours"
              size="sm"
              :options="windowOptions"
              style="max-width: 160px;"
            />
            <feather
              v-b-tooltip.hover
              type="help-circle"
              class="icon-xs help-tip ml-2"
              title="Worst-case latency/jitter (left axis, ms) and loss (right axis, %) per 5-minute sample. Amber vertical markers are path events of this device in the window."
            />
          </div>
          <div
            v-if="hasChartData"
            class="sdwan-chart"
          >
            <apexchart
              type="line"
              height="280"
              :series="chartSeries"
              :options="chartOptions"
            />
          </div>
          <div
            v-else
            class="text-muted py-4 text-center"
          >
            Not enough samples for {{ selectedVif || 'this vif' }} in this window
            (fast-tier samples arrive every 5 minutes).
          </div>
        </b-tab>

        <!-- Recent path events -->
        <b-tab>
          <template v-slot:title>
            <feather
              type="activity"
              class="icon-xs mr-1"
            />
            Events ({{ (pathstats.events || []).length }})
          </template>
          <div
            v-if="(pathstats.events || []).length"
            class="table-responsive"
            style="max-height: min(420px, 45vh); overflow-y: auto;"
          >
            <table class="table table-sm font-size-12 mb-0">
              <thead>
                <tr class="text-muted">
                  <th style="width: 30px;" />
                  <th>Collected</th>
                  <th>Device time</th>
                  <th>Path</th>
                  <th>Reason</th>
                  <th>Policy</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(event, index) in pathstats.events"
                  :key="`event-${index}`"
                  :class="{ 'sdwan-switch-row': eventKind(event) === 'switch' }"
                >
                  <td class="text-center">
                    <feather
                      :type="eventIcon(event)"
                      class="icon-xs"
                      :class="`text-${eventVariant(event)}`"
                    />
                  </td>
                  <td class="text-nowrap text-muted">
                    {{ formatDate(event.ts) }}
                  </td>
                  <td class="text-nowrap text-muted">
                    {{ event.tick || '—' }}
                  </td>
                  <td class="text-nowrap">
                    <template v-if="eventKind(event) === 'switch'">
                      {{ event.from }}
                      <feather
                        type="arrow-right"
                        class="icon-xs text-warning mx-1"
                      />
                      <span class="font-weight-bold">{{ event.to }}</span>
                    </template>
                    <template v-else>
                      <span class="text-muted">{{ event.to || '—' }}</span>
                    </template>
                  </td>
                  <td>
                    <b-badge :variant="eventVariant(event) === 'warning' ? 'warning' : 'light'">
                      {{ event.reason || '—' }}
                    </b-badge>
                  </td>
                  <td class="text-muted">
                    {{ event.policy || '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="text-muted py-3 text-center"
          >
            No path events in this window.
          </div>
        </b-tab>

        <!-- Collector errors -->
        <b-tab>
          <template v-slot:title>
            <feather
              type="alert-triangle"
              class="icon-xs mr-1"
            />
            Errors ({{ (detail.errors || []).length }})
          </template>
          <div
            v-if="(detail.errors || []).length"
            class="table-responsive"
            style="max-height: min(420px, 45vh); overflow-y: auto;"
          >
            <table class="table table-sm font-size-12 mb-0">
              <thead>
                <tr class="text-muted">
                  <th>Time</th>
                  <th>Report</th>
                  <th>Type</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(error, index) in detail.errors"
                  :key="`error-${index}`"
                >
                  <td class="text-nowrap">
                    {{ formatDate(error.ts) }}
                  </td>
                  <td>{{ error.report }}</td>
                  <td>
                    <b-badge variant="secondary">
                      {{ error.error_type }}
                    </b-badge>
                  </td>
                  <td>{{ error.meaning }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="text-muted py-3 text-center"
          >
            No collector errors in the last 7 days.
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<style scoped>
/* Keep the chart inside the dialog: Safari lets an oversized SVG paint over
   the modal edge instead of clipping it. */
.sdwan-chart {
  overflow: hidden;
  max-width: 100%;
}

.help-tip {
  cursor: help;
  color: #ced4da;
}
.help-tip:hover {
  color: #98a6ad;
}
.sdwan-switch-row {
  background-color: #fff8e6;
}
.sdwan-switch-row td:first-child {
  box-shadow: inset 3px 0 0 #ffbc00;
}
</style>
